import {
  Box,
  Flex,
  Progress,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Skeleton,
  useColorModeValue,
  Button,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import smartTruncate from 'smart-truncate';
import Order from '../../pages/Market/Order/Order';
import isEmpty from 'is-empty';
import logger from '../../utils/logger';

const Sidebar = () => {
  const {
    accountAddress,
    accountBalance,
    accountDetails,
    isDataLoaded,
    noWalletDetect,
    totalFrozenBalance,
    resourceDetails,
  } = useSelector((state) => state.account);
  const [energyProgress, setEnergyProgress] = useState(0);
  const [bandwidthProgress, setBandwidthProgress] = useState(0);
  const toast = useToast();

  // Move all hooks to the top level
  const bgColor = useColorModeValue('#fff', '#3e3d3c');
  const grayBgColor = useColorModeValue('#f7f7f7', '#2d2d2d');
  const textColor = useColorModeValue('gray.600', '#a0aec0');

  const handleConnectWallet = async () => {
    try {
      if (window.tronWeb && window.tronWeb.ready) {
        toast({
          title: 'Wallet already connected',
          description: 'Your TronLink wallet is already connected.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } else if (window.tronWeb) {
        toast({
          title: 'Please unlock your wallet',
          description: 'Please unlock your TronLink wallet and refresh the page.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Wallet not detected',
          description: 'Please install TronLink wallet extension.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        // Open TronLink download page
        window.open('https://www.tronlink.org/', '_blank');
      }
    } catch (error) {
      logger.error('Error connecting wallet:', error);
      toast({
        title: 'Connection error',
        description: 'Failed to connect wallet. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (
      resourceDetails?.energyRemaining !== undefined &&
      resourceDetails?.energyLimit !== undefined &&
      resourceDetails?.energyLimit > 0
    ) {
      setEnergyProgress(
        (resourceDetails?.energyRemaining /
          resourceDetails?.energyLimit) *
          100
      );
    }
    if (
      resourceDetails?.netRemaining !== undefined &&
      resourceDetails?.netLimit !== undefined
    ) {
      const totalBandwidth = (resourceDetails?.netRemaining || 0) + (resourceDetails?.freeNetRemaining || 0);
      const totalBandwidthLimit = (resourceDetails?.netLimit || 0) + (resourceDetails?.freeNetLimit || 0);

      if (totalBandwidthLimit > 0) {
        setBandwidthProgress((totalBandwidth / totalBandwidthLimit) * 100);
      }
    }
  }, [resourceDetails, isDataLoaded, noWalletDetect, accountBalance, totalFrozenBalance]);

  return (
    <Flex width={{ base: '100%', lg: '300px' }} flexDir={'column'} mb='20px' px={{ base: '10px', lg: '0' }}>
      {noWalletDetect || !accountAddress ? (
        // Show Connect Wallet button when no wallet is connected
        <Box
          boxShadow={'0 10px 20px rgba(0,0,0,.05)'}
          borderRadius={'10px'}
          backgroundColor={bgColor}
          mb='10px'
          p={{ base: '30px 20px', lg: '20px' }}
          textAlign='center'
        >
          <Text fontSize={'18px'} fontWeight={'600'} mb='15px'>
            Welcome to TronWeb
          </Text>
          <Text fontSize={'14px'} color={textColor} mb='20px'>
            Connect your wallet to get started
          </Text>
          <Button
            onClick={handleConnectWallet}
            colorScheme='red'
            backgroundColor='#e31e24'
            color='white'
            _hover={{ backgroundColor: '#c11a1f' }}
            width='100%'
            size='lg'
            fontWeight={'600'}
          >
            Connect Wallet
          </Button>
        </Box>
      ) : (
        // Show Account Overview when wallet is connected
        <Accordion defaultIndex={[1]} allowMultiple>
          <AccordionItem
            boxShadow={'0 10px 20px rgba(0,0,0,.05)'}
            borderRadius={'10px'}
            backgroundColor={bgColor}
            mb='10px'
          >
            <h2>
              <AccordionButton>
                <Box as='span' flex='1' textAlign='left'>
                  Account Overview
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
            <Box width={'100%'} height={'fit-content'}>
              <Text mt='5px' fontSize={'12px'} textAlign={'center'} color={'gray.600'}>
                <a
                  href={`https://tronscan.org/#/address/${accountAddress}`}
                  target='_blank'
                  rel='noreferrer'
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  {smartTruncate(accountAddress, 35, { position: 8 })}
                </a>
              </Text>

              <Box backgroundColor={grayBgColor} p={'20px'} borderRadius={'10px'} mt='15px'>
                {/* Main Balance Display */}
                <Flex
                  mb='20px'
                  align={'center'}
                  justify={'center'}
                  fontSize={'20px'}
                  fontWeight={'600'}
                  gap={'8px'}
                  flexWrap={'wrap'}
                >
                  <Text className='text-primary' color={'#e31e24'} fontSize={'32px'}>
                    {((accountBalance || 0) + (totalFrozenBalance || 0))?.toFixed(2)}{' '}
                    TRX
                  </Text>
                  <Text fontSize={'16px'} fontWeight={'400'} color={'gray.600'}>
                    / Available {(accountBalance || 0)?.toFixed(2)} TRX
                  </Text>
                </Flex>

                {/* Staked and Vote Available */}
                <Flex justify={'space-between'} mb='20px'>
                  <Box>
                    <Text fontSize={'24px'} fontWeight={'600'}>
                      {!isDataLoaded && !noWalletDetect ? (
                        <Skeleton
                          height='30px'
                          width='60px'
                          mt='2'
                        />
                      ) : (
                        (totalFrozenBalance || 0)?.toFixed(2)
                      )}{' '}
                      <Text as='span' fontSize={'16px'} fontWeight={'400'}>TRX</Text>
                    </Text>
                    <Text fontSize={'14px'} color={'gray.600'} mt='5px'>Staked</Text>
                  </Box>
                  <Box textAlign={'right'}>
                    <Text fontSize={'24px'} fontWeight={'600'}>
                      0 <Text as='span' fontSize={'16px'} fontWeight={'400'}>TRX</Text>
                    </Text>
                    <Text fontSize={'14px'} color={'gray.600'} mt='5px'>Vote Available</Text>
                  </Box>
                </Flex>
              </Box>

              {/* Energy and Bandwidth Section */}
              <Flex mt='20px' justify={'space-between'} gap={'20px'}>
                <Box width={'50%'}>
                  <Text fontSize={'14px'} mb='8px' fontWeight={'500'} color={'gray.600'}>
                    Energy
                  </Text>
                  {!isDataLoaded && !noWalletDetect ? (
                    <>
                      <Skeleton height='20px' width='80px' mb='8px' />
                      <Progress
                        value={0}
                        colorScheme='red'
                        borderRadius={'5px'}
                        height={'8px'}
                        isIndeterminate
                      />
                    </>
                  ) : (
                    <>
                      <Text fontSize={'16px'} fontWeight={'600'} mb='8px'>
                        {resourceDetails?.energyRemaining !== undefined
                          ? (resourceDetails?.energyRemaining || 0) +
                            ' / ' +
                            (resourceDetails?.energyLimit || 0)
                          : '0 / 0'}
                      </Text>
                      <Progress
                        value={energyProgress}
                        colorScheme='red'
                        borderRadius={'5px'}
                        height={'8px'}
                      />
                    </>
                  )}
                </Box>
                <Box width={'50%'}>
                  <Text fontSize={'14px'} mb='8px' fontWeight={'500'} color={'gray.600'}>
                    Bandwidth
                  </Text>
                  {!isDataLoaded && !noWalletDetect ? (
                    <>
                      <Skeleton height='20px' width='80px' mb='8px' />
                      <Progress
                        value={0}
                        colorScheme='red'
                        borderRadius={'5px'}
                        height={'8px'}
                        isIndeterminate
                      />
                    </>
                  ) : (
                    <>
                      <Text fontSize={'16px'} fontWeight={'600'} mb='8px'>
                        {resourceDetails?.netRemaining !== undefined
                          ? ((resourceDetails?.netRemaining || 0) +
                              (resourceDetails?.freeNetRemaining || 0)) +
                            ' / ' +
                            ((resourceDetails?.netLimit || 0) +
                              (resourceDetails?.freeNetLimit || 0))
                          : '0 / 0'}
                      </Text>
                      <Progress
                        value={bandwidthProgress}
                        colorScheme='red'
                        borderRadius={'5px'}
                        height={'8px'}
                      />
                    </>
                  )}
                </Box>
              </Flex>
            </Box>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      )}
      <Box
        boxShadow={'0 10px 20px rgba(0,0,0,.05)'}
        borderRadius={'10px'}
        width={'100%'}
        height={'fit-content'}
        padding={'20px'}
        backgroundColor={bgColor}
      >
        <Order />
      </Box>
    </Flex>
  );
};

export default Sidebar;
